// useInactivityTimer.js - Custom hook for handling automatic logout on inactivity

import { useCallback, useEffect, useRef } from 'react';
import { INACTIVITY_CONFIG } from '../config/inactivityConfig';

const useInactivityTimer = (timeoutMinutes = INACTIVITY_CONFIG.DEFAULT_TIMEOUT_MINUTES) => {
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const isActiveRef = useRef(true);
  const lastActivityRef = useRef(Date.now());

  // Configuration
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds
  const WARNING_TIME_MS = INACTIVITY_CONFIG.WARNING_TIME_MINUTES * 60 * 1000; // Show warning before logout
  const WARNING_TIMEOUT_MS = TIMEOUT_MS - WARNING_TIME_MS;

  // Clean logout function
  const performLogout = useCallback(() => {
    if (INACTIVITY_CONFIG.DEBUG_LOGS) {
      console.log('🔒 Automatic logout due to inactivity');
    }
    
    // Mark as inactive to prevent further timer operations
    isActiveRef.current = false;
    
    // Clear any intervals or timeouts that might be running
    clearTimeout(timeoutRef.current);
    clearTimeout(warningTimeoutRef.current);
    
    // Clear all auth data
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('last_mt5_account_id');
    
    // Clear any session cookies
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    // Force navigation using window.location instead of navigate
    // This ensures navigation works even if React Router is in a loading state
    window.location.href = '/login?reason=inactivity';
    
    // Show alert after navigation
    setTimeout(() => {
      alert(INACTIVITY_CONFIG.MESSAGES.LOGOUT_ALERT);
    }, 500);
  }, []);
  // Show warning before logout  
  const showLogoutWarning = useCallback(() => {
    if (!isActiveRef.current) return;
    
    if (INACTIVITY_CONFIG.DEBUG_LOGS) {
      console.log('⚠️ Showing inactivity warning');
    }
    
    // Use confirm dialog - OK means stay logged in, Cancel means logout now
    const stayLoggedIn = window.confirm(INACTIVITY_CONFIG.MESSAGES.WARNING);
    
    if (stayLoggedIn) {
      // User wants to stay logged in - manually reset timer
      if (INACTIVITY_CONFIG.DEBUG_LOGS) {
        console.log('👤 User chose to stay logged in, resetting timer');
      }
      
      // Reset timer manually here to avoid circular dependency
      lastActivityRef.current = Date.now();
      
      // Clear existing timers
      clearTimeout(timeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
      
      // Set warning timer (shows warning before logout)
      warningTimeoutRef.current = setTimeout(() => {
        if (isActiveRef.current) {
          showLogoutWarning();
        }
      }, WARNING_TIMEOUT_MS);
      
      // Set final logout timer
      timeoutRef.current = setTimeout(() => {
        if (isActiveRef.current) {
          performLogout();
        }
      }, TIMEOUT_MS);
    } else {
      // User chose to logout now or closed the dialog
      if (INACTIVITY_CONFIG.DEBUG_LOGS) {
        console.log('👤 User chose to logout or dismissed warning');
      }
      performLogout();
    }
  }, [performLogout, WARNING_TIMEOUT_MS, TIMEOUT_MS]);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (!isActiveRef.current) return;
    
    lastActivityRef.current = Date.now();
    
    // Clear existing timers
    clearTimeout(timeoutRef.current);
    clearTimeout(warningTimeoutRef.current);
    
    if (INACTIVITY_CONFIG.DEBUG_LOGS) {
      console.log(`⏰ Inactivity timer reset. Logout in ${timeoutMinutes} minutes`);
    }
    
    // Set warning timer (shows warning before logout)
    warningTimeoutRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        showLogoutWarning();
      }
    }, WARNING_TIMEOUT_MS);
    
    // Set final logout timer
    timeoutRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        performLogout();
      }
    }, TIMEOUT_MS);
  }, [timeoutMinutes, showLogoutWarning, performLogout, WARNING_TIMEOUT_MS, TIMEOUT_MS]);

  // Check if the page is visible/focused
  const isPageVisible = useCallback(() => {
    return !document.hidden && document.hasFocus();
  }, []);

  // Handle activity events
  const handleActivity = useCallback(() => {
    // Only reset timer if page is visible and focused
    if (isPageVisible()) {
      resetTimer();
    }
  }, [resetTimer, isPageVisible]);

  // Handle visibility change
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Page is hidden - record the time
      if (INACTIVITY_CONFIG.DEBUG_LOGS) {
        console.log('📱 Page hidden - inactivity timer continues');
      }
    } else {
      // Page is visible again - check how long it was hidden
      const hiddenTime = Date.now() - lastActivityRef.current;
      if (INACTIVITY_CONFIG.DEBUG_LOGS) {
        console.log(`👁️ Page visible again after ${Math.round(hiddenTime / 1000)}s`);
      }
      
      // If hidden for more than the timeout period, logout immediately
      if (hiddenTime >= TIMEOUT_MS) {
        if (INACTIVITY_CONFIG.DEBUG_LOGS) {
          console.log('🔒 Page was hidden too long, logging out immediately');
        }
        performLogout();
      } else {
        // Reset timer with remaining time
        resetTimer();
      }
    }
  }, [performLogout, resetTimer, TIMEOUT_MS]);

  // Initialize the timer and event listeners
  useEffect(() => {
    if (INACTIVITY_CONFIG.DEBUG_LOGS) {
      console.log(`🚀 Inactivity timer initialized: ${timeoutMinutes} minutes`);
    }
    
    isActiveRef.current = true;
    
    // Add event listeners for user activity
    INACTIVITY_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Add listener for page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Start the timer
    resetTimer();
    
    // Cleanup function
    return () => {
      if (INACTIVITY_CONFIG.DEBUG_LOGS) {
        console.log('🛑 Inactivity timer cleanup');
      }
      isActiveRef.current = false;
      
      // Remove event listeners
      INACTIVITY_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Clear timers
      clearTimeout(timeoutRef.current);
      clearTimeout(warningTimeoutRef.current);
    };
  }, [handleActivity, handleVisibilityChange, resetTimer, timeoutMinutes]);

  // Return functions that components can use
  return {
    resetTimer,
    getRemainingTime: () => {
      const elapsed = Date.now() - lastActivityRef.current;
      return Math.max(0, TIMEOUT_MS - elapsed);
    },
    isActive: () => isActiveRef.current
  };
};

export default useInactivityTimer;
